import { useEffect, useState } from 'react';
import api from '../../../services/api';
import { accessWord, listWords } from './words';

// PLUGINS, NAMED OUTSIDE THE PLUGINS SECTION — a realm's or a contract's
// Tools (AccessList), the consent page's ask (grant/Ask.js): a
// contract stores plugins and tools by id (`plugins.execute.plugin_ids`,
// `tools: { <plugin_id>: { <tool_id>: mode } }`), and a person reads them by
// name, "Mailroom · Send email: asks you". The names come from the plugin
// list (GET /plugins), read once per page load and shared.

let names = null; // a promise of { <plugin id>: { name, tools: { <tool id>: name } } }

/** Every plugin's name and its tools' names, by id. A failed read names nothing (and is read again next time). */
export function loadPluginNames() {
    if (!names) {
        names = Promise.resolve()
            .then(() => api.getPlugins())
            .then((r) => Object.fromEntries((Array.isArray(r) ? r : r?.plugins || []).filter((p) => p?.id).map((p) => [String(p.id), {
                name: p.display_name || p.name || null,
                tools: Object.fromEntries((p.tools || []).filter((t) => t?.tool_id).map((t) => [String(t.tool_id), t.name || null])),
            }])))
            .catch(() => { names = null; return {}; });
    }
    return names;
}
/** For tests: forget what was read. */
export const forgetPluginNames = () => { names = null; };

/** The names, once read ({} until then); nothing is read while `enabled` is false. */
export function usePluginNames(enabled = true) {
    const [got, setGot] = useState({});
    useEffect(() => {
        if (!enabled) return undefined;
        let live = true;
        loadPluginNames().then((n) => { if (live) setGot(n || {}); });
        return () => { live = false; };
    }, [enabled]);
    return got;
}

const humanise = (s) => {
    const t = String(s || '').replace(/[_-]+/g, ' ').trim();
    return t ? `${t[0].toUpperCase()}${t.slice(1)}` : t;
};

/** A plugin's name, else "A plugin" (never its id). */
export const pluginNameOf = (names, pluginId) => names?.[String(pluginId)]?.name || 'A plugin';
/** "Mailroom · Send email", or the plugin alone for its one tool. A tool not
 *  in the list (not read yet, or gone) is its id said plainly. */
export function toolNameOf(names, pluginId, toolId) {
    const plugin = pluginNameOf(names, pluginId);
    if (toolId == null || toolId === '') return plugin;
    const tool = names?.[String(pluginId)]?.tools?.[String(toolId)] || humanise(toolId);
    return `${plugin} · ${tool}`;
}
// WHAT EACH TOOL ASKS FOR (GET /tools: its writes, whether it can't be undone,
// and its reads where the server sends them), by "<plugin id>:<tool id>", read
// once per page load. A failed read says nothing more than the names.
let facts = null;
export function loadToolFacts() {
    if (!facts) {
        facts = Promise.resolve()
            .then(() => api.request('/tools?limit=1000'))
            .then((r) => Object.fromEntries((r?.tools || []).filter((t) => t?.plugin?.id).map((t) => [`${t.plugin.id}:${t.tool_id ?? ''}`, {
                reads: Array.isArray(t.reads) ? t.reads : null,
                writes: Array.isArray(t.writes) ? t.writes : null,
                irreversible: !!t.irreversible,
            }])))
            .catch(() => { facts = null; return {}; });
    }
    return facts;
}
export const forgetToolFacts = () => { facts = null; };
/** What each tool asks for, once read ({} until then). */
export function useToolFacts(enabled = true) {
    const [got, setGot] = useState({});
    useEffect(() => {
        if (!enabled) return undefined;
        let live = true;
        loadToolFacts().then((f) => { if (live) setGot(f || {}); });
        return () => { live = false; };
    }, [enabled]);
    return got;
}

/** "Mailroom · Send email: asks you" (Allowed / Asks you / Never, in a line). */
export const toolModeLine = (names, { pluginId, toolId, mode }) => `${toolNameOf(names, pluginId, toolId)}: ${accessWord(mode).toLowerCase()}`;
/** Which plugins a contract's tools reach: "Only Mailroom and Notion Pages"; one
 *  not named in the list is counted ("Only Mailroom and 1 other plugin"). */
export function onlyPluginsLine(names, ids) {
    if (!ids?.length) return null;
    const known = ids.map((id) => names?.[String(id)]?.name).filter(Boolean);
    const rest = ids.length - known.length;
    if (!known.length) return `Only ${rest} ${rest === 1 ? 'plugin' : 'plugins'}`;
    return `Only ${listWords([...known, ...(rest ? [`${rest} other ${rest === 1 ? 'plugin' : 'plugins'}`] : [])])}`;
}
